// P-085 · lo que la ayuda necesita saber, en funciones puras (se prueban sin pantalla).
//
// «Datos para soporte»: lo justo para que el operador encuentre el negocio y entienda dónde estaba la persona, sin nada
// secreto: nunca la contraseña, el correo, un token, una cookie ni un identificador de un registro. La pantalla se toma
// del hash SIN su parte `?…` (ahí viajan los tokens de los enlaces) y sin ids.

export interface SupportContext {
  businessName?: string | null;
  businessSlug?: string | null;
  storeLabel?: string | null;
  role?: 'OWNER' | 'MANAGER' | 'EMPLOYEE' | null;
  /** La pantalla, ya limpia (ver `screenOf`). */
  screen: string;
  version: string;
  at: Date;
  userAgent: string;
}

const ROLE_LABEL: Record<'OWNER' | 'MANAGER' | 'EMPLOYEE', string> = { OWNER: 'Dueño', MANAGER: 'Gerente', EMPLOYEE: 'Empleado' };
const UUID_PREFIX = /^[0-9a-f]{8}-[0-9a-f]{4}-/i;

/** La pantalla actual a partir del hash: sin `#`, sin la parte `?…`, a lo más dos niveles y sin identificadores. */
export function screenOf(hash: string): string {
  const path = hash.replace(/^#/, '').split('?')[0];
  const segments = path.split('/').filter(Boolean).slice(0, 2).filter((segment) => !UUID_PREFIX.test(segment));
  return segments.join('/') || 'inicio';
}

/**
 * La versión de la app: el nombre del archivo principal que el navegador cargó (`index-XXXX.js`), el mismo que se usa
 * para comprobar un despliegue. En desarrollo no hay archivo con huella: «desarrollo».
 */
export function appVersion(doc: Pick<Document, 'querySelector'> = document): string {
  const script = doc.querySelector('script[type="module"][src*="assets/"]');
  const name = (script?.getAttribute('src') ?? '').split('/').pop() ?? '';
  return /^index-[A-Za-z0-9_-]+\.js$/.test(name) ? name : 'desarrollo';
}

export function shortUserAgent(userAgent: string): string {
  return userAgent.replace(/\s+/g, ' ').trim().slice(0, 140);
}

const DATE_TIME = new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short' });

/** Los renglones de «Datos para soporte», en el orden en que se leen. */
export function supportSummary(ctx: SupportContext): string[] {
  const lines: string[] = [];
  if (ctx.businessName) lines.push(`Negocio: ${ctx.businessName}${ctx.businessSlug ? ` (${ctx.businessSlug})` : ''}`);
  if (ctx.storeLabel) lines.push(`Tienda: ${ctx.storeLabel}`);
  if (ctx.role) lines.push(`Rol: ${ROLE_LABEL[ctx.role]}`);
  lines.push(`Pantalla: ${ctx.screen}`);
  lines.push(`Versión de la app: ${ctx.version}`);
  lines.push(`Fecha: ${DATE_TIME.format(ctx.at)}`);
  lines.push(`Navegador: ${shortUserAgent(ctx.userAgent)}`);
  return lines;
}

const MESSAGE_START = 'Hola, necesito ayuda con mi negocio.';

/** Un correo ya escrito con los datos de soporte (el correo de ayuda llega validado del servidor). */
export function mailtoHref(email: string, lines: string[]): string {
  const subject = encodeURIComponent('Necesito ayuda');
  const body = encodeURIComponent(`${MESSAGE_START}\n\n${lines.join('\n')}`);
  return `mailto:${email}?subject=${subject}&body=${body}`;
}

/** Un mensaje de WhatsApp ya escrito con los datos de soporte (el número llega del servidor, solo dígitos). */
export function whatsappHref(digits: string, lines: string[]): string {
  return `https://wa.me/${digits}?text=${encodeURIComponent(`${MESSAGE_START}\n\n${lines.join('\n')}`)}`;
}

/** El contexto de una pantalla SIN sesión (registro, invitación, inicio de sesión). */
export function publicSupportContext(hash: string, now = new Date()): SupportContext {
  return { screen: screenOf(hash), version: appVersion(), at: now, userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent };
}
